import { Link } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ArrowUpRight, ArrowRight, Check, X, Truck, Sparkles, ShieldCheck, CalendarCheck2, MonitorSmartphone } from "lucide-react";
import { IMAGES, waLink, SISTER_BRAND } from "@/lib/constants";
import { Pic } from "@/components/Pic";
import { STATIONERY_NAV } from "@/lib/stationeryNav";
import { useReveal, useCountUp } from "@/lib/useReveal";
import Flourish from "@/components/Flourish";
import FloatingPetals from "@/components/FloatingPetals";
import ProductStrip from "@/components/ProductStrip";
import PageSEO from "@/components/PageSEO";
import { SEO } from "@/lib/seoConfig";

// Below-the-fold components — code-split so they don't block initial paint.
// Speeds up mobile LCP by ~40%.
const CourierLogos = lazy(() => import("@/components/CourierLogos"));
const JournalTeaser = lazy(() => import("@/components/JournalTeaser").then((m) => ({ default: m.JournalTeaser })));
const FeaturedOn = lazy(() => import("@/components/FeaturedOn"));
const TrustStrip = lazy(() => import("@/components/TrustStrip"));
const WorldwideShipping = lazy(() => import("@/components/WorldwideShipping"));
const Lookbook = lazy(() => import("@/components/Lookbook"));
const FAQ = lazy(() => import("@/components/FAQ"));
const SampleKit = lazy(() => import("@/components/SampleKit"));
const Testimonials = lazy(() => import("@/components/Testimonials"));

const Skeleton = () => <div style={{ minHeight: 200, background: "#0E0709" }} />;

const services = [
  { title: "Luggage Tags", tag: "Custom shapes · Foil · Multi-piece sets", img: IMAGES.luggageTags, slug: "luggage-tags" },
  { title: "Caricature Luggage Tags", tag: "Hand-illustrated · Couple portraits", img: IMAGES.caricatureTags, slug: "caricature" },
  { title: "Hamper Tags & Door Danglers", tag: "Welcome rooms · Monogrammed", img: IMAGES.hamperTags, slug: "hamper-tags" },
  { title: "Tent Cards", tag: "Table seating · Place cards · Menus", img: IMAGES.tentCards, slug: "tent-cards" },
];

const compareRows = [
  { label: "Pricing model", others: "Premium price for premium feel", us: "Premium feel at fair pricing" },
  { label: "Quality control", others: "2 – 3 quick reviews", us: "10-point detailed inspection" },
  { label: "Delivery network", others: "Selected cities only", us: "Pan-India + International" },
  { label: "On-time guarantee", others: "Frequent delays", us: "Dispatch on schedule, every time" },
  { label: "Logistics partners", others: "Local couriers", us: "DTDC · Blue Dart · Express" },
];

const steps = [
  { n: "01", t: "Design", d: "A bespoke direction shaped around your brand, season, or moment." },
  { n: "02", t: "10-Point Audit", d: "Colour, paper, finishing, packaging — every detail inspected." },
  { n: "03", t: "Print", d: "World-class print with premium stocks, foils, embossing & beyond." },
  { n: "04", t: "Deliver", d: "DTDC and Blue Dart partners ship across India and overseas." },
];

function Stat({ value, suffix, label, delay = 0 }) {
  const { ref, seen } = useReveal();
  const v = useCountUp(value, { start: seen, duration: 1800 });
  return (
    <div ref={ref} className={`reveal reveal-d${delay} border-l border-[#3A2530] pl-5`}>
      <p className="font-heading text-3xl md:text-4xl font-normal text-[#F0C4BC] tabular">{v}{suffix}</p>
      <p className="overline text-[#C4A8B0] mt-2">{label}</p>
    </div>
  );
}

function Reveal({ as: Tag = "div", className = "", delay = 0, children, ...rest }) {
  const { ref } = useReveal();
  return (
    <Tag ref={ref} className={`reveal reveal-d${delay} ${className}`} {...rest}>{children}</Tag>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      <PageSEO {...SEO.home} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <FloatingPetals count={16} />
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 pt-6 md:pt-14 pb-10 md:pb-20 relative z-10">
          <div className="flex items-center justify-between mb-6 md:mb-14">
            <p className="overline shimmer-text">Wedding Stationery · Print Atelier</p>
            <p className="overline text-[#C4A8B0] hidden md:block font-mono">№ 001 — MMXXVI</p>
          </div>

          <div className="flex justify-center mb-4 md:mb-6">
            <Flourish className="w-32 md:w-44 opacity-60" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 md:gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-heading font-normal text-[10vw] sm:text-[8vw] md:text-[6.5vw] lg:text-[5.4vw] xl:text-[88px] leading-[0.95] tracking-[-0.015em] text-[#F8EDE7]">
                <span className="block split-line in"><span>Custom Wedding</span></span>
                <span className="block split-line d2 in"><span>Invitation Cards —</span></span>
                <span className="block italic-serif text-[#F0C4BC] split-line d3 in"><span>Designed &amp; Printed</span></span>
                <span className="block italic-serif text-[#F0C4BC] split-line d3 in"><span>just for you.</span></span>
              </h1>

              <Reveal delay={3} className="mt-6 md:mt-9 max-w-2xl">
                <p className="text-base md:text-lg font-normal text-[#E8D5DA] leading-relaxed">
                  <span className="text-[#F0C4BC] font-medium">100% Personalized</span>
                  <span className="text-[#C4A8B0] mx-2">•</span>
                  <span className="text-[#F0C4BC] font-medium">Pan India Delivery</span>
                  <span className="text-[#C4A8B0] mx-2">•</span>
                  <span className="text-[#F0C4BC] font-medium">International Shipping Available</span>
                </p>
              </Reveal>

              <Reveal delay={4} className="mt-10 flex flex-wrap gap-4">
                <a
                  href={waLink("Hi! I'd like a FREE consultation for custom wedding invitation cards.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-cta"
                  className="btn-luxe group inline-flex items-center gap-3 bg-[#25D366] text-white px-9 py-[18px] text-[12px] tracking-[0.22em] uppercase hover:opacity-95"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.9-2-1s-.5-.1-.7.2-.8 1-1 1.2-.4.2-.7.1c-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1-1.1 2.5s1.1 2.9 1.3 3.1c.2.2 2.2 3.4 5.4 4.8.8.3 1.3.5 1.8.7.7.2 1.4.2 1.9.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4 0-.1-.3-.2-.5-.3zM12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.7 1.5 5.3L2 22l4.8-1.5C8.3 21.5 10.1 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2z"/></svg>
                  WhatsApp Us Now
                </a>
                <Link
                  to="/services"
                  data-testid="hero-explore-cta"
                  className="btn-outline-luxe group inline-flex items-center gap-3 border border-[#F8EDE7] text-[#F8EDE7] px-9 py-[18px] text-[12px] tracking-[0.22em] uppercase"
                >
                  Explore Collection
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>

              <Reveal delay={5} className="mt-5">
                <p data-testid="hero-consult-line" className="inline-flex items-center gap-2 text-sm md:text-base text-[#F8EDE7]/85">
                  <span className="text-base md:text-lg">📞</span>
                  <span>Call or WhatsApp for <strong className="text-[#F0C4BC] font-medium">FREE Consultation</strong> &amp; Sample Viewing</span>
                </p>
              </Reveal>

              <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
                <Stat value={24} suffix="-hr" label="Delhi NCR Express" delay={1} />
                <Stat value={5000} suffix="+" label="Pieces Crafted" delay={2} />
                <Stat value={100} suffix="%" label="On-time Delivery" delay={3} />
              </div>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={2} className="relative">
                <div className="relative aspect-[4/5] overflow-hidden hero-shine">
                  <div className="absolute inset-0 reveal-img in">
                    <picture className="contents">
                      <source
                        type="image/avif"
                        srcSet="/images/portfolio/luxury-wedding-invitation-fan-480.avif 480w, /images/portfolio/luxury-wedding-invitation-fan.avif 880w"
                        sizes="(max-width: 768px) 92vw, 44vw"
                      />
                      <source
                        type="image/webp"
                        srcSet="/images/portfolio/luxury-wedding-invitation-fan-480.webp 480w, /images/portfolio/luxury-wedding-invitation-fan.webp 880w"
                        sizes="(max-width: 768px) 92vw, 44vw"
                      />
                      <img
                        src="/images/portfolio/luxury-wedding-invitation-fan.webp"
                        alt="Luxury custom wedding fan invitation card, hand-illustrated and foil-finished by StationeryWalaa"
                        width="880"
                        height="1206"
                        fetchPriority="high"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-[1800ms] ease-out hover:scale-[1.06]"
                      />
                    </picture>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#F8EDE7]/55 via-[#F8EDE7]/0 to-transparent p-7">
                    <p className="overline text-[#0E0709]/85">Featured · The Heirloom Series</p>
                    <p className="font-heading text-3xl text-[#0E0709] mt-2 italic-serif">Wedding Fan Invites</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] text-[#C4A8B0] tracking-[0.18em] uppercase">
                  <span>—— Crafted in India</span>
                  <span className="font-mono">2026 · Volume I</span>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-[#3A2530] bg-[#1A0E12] overflow-hidden py-5">
          <div className="marquee-track flex gap-14 whitespace-nowrap">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-14 items-center text-[15px] font-normal text-[#E8D5DA]">
                {["Luggage Tags","Caricature Tags","Hamper Tags","Door Danglers","Tent Cards","Wax Seals","Petal Cones","Welcome Cards"].map((w, j) => (
                  <span key={j} className="flex items-center gap-14">
                    <span className="font-heading text-2xl italic-serif text-[#F0C4BC]">{w}</span>
                    <span className="text-[#E8B4AC]">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT STRIP — instant "this is wedding stationery" vibe */}
      <ProductStrip />

      {/* COURIER PARTNERS */}
      <Suspense fallback={<Skeleton />}>
        <div className="lazy-paint">
          <CourierLogos dark={true} />

          {/* FEATURED ON — WeddingSutra · Wedding Bazaar */}
          <FeaturedOn />

          {/* WORLDWIDE SHIPPING — SEO + trust */}
          <WorldwideShipping />

          {/* TRUST STRIP */}
          <TrustStrip />
        </div>
      </Suspense>

      {/* WHY CHOOSE US */}
      <section data-testid="section-why-us" className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <Reveal>
              <p className="overline text-[#C4A8B0] mb-6">Why Choose Us</p>
              <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl leading-[0.92] tracking-tight font-normal">
                Don&apos;t compare.<br/>
                <span className="italic-serif text-[#F0C4BC]">Experience.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={2} className="md:col-span-7 text-[#E8D5DA] text-base md:text-lg font-normal leading-relaxed self-end">
            Every studio claims to be the best. We let the paper, the print, and the promise speak. Below, where most stop — and where we begin.
          </Reveal>
        </div>

        <Reveal delay={1} className="relative grid grid-cols-1 md:grid-cols-2 gap-px bg-[#3A2530] border border-[#3A2530]">
          {/* OTHERS — muted column */}
          <div className="bg-[#1A0E12] p-9 md:p-14 relative">
            <div className="flex items-center gap-4 mb-8">
              <span className="overline text-[#C4A8B0]">— Most studios</span>
              <span className="ml-auto inline-flex items-center justify-center w-12 h-12 border border-[#3A2530] text-[#C4A8B0]">
                <X className="w-5 h-5" />
              </span>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl text-[#C4A8B0] font-normal mb-1">Others</h3>
            <p className="text-xs tracking-[0.18em] uppercase text-[#C4A8B0]/60">Score · 4 / 10</p>

            <ul className="mt-10 space-y-7">
              {compareRows.map((row, i) => (
                <li key={row.label}>
                  <p className="overline text-[#C4A8B0]/70 mb-2">{row.label}</p>
                  <p className="text-base md:text-lg text-[#C4A8B0] font-normal leading-snug">{row.others}</p>
                  <div className="mt-3 h-px bg-[#3A2530]">
                    <div className="h-full bg-[#C4A8B0]/40" style={{ width: `${[35, 25, 30, 20, 30][i]}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* STATIONARY WALA — featured column */}
          <div className="bg-gradient-to-b from-[#2A1820] to-[#1F1218] p-9 md:p-14 relative">
            <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F0C4BC] to-transparent" />
            <div className="absolute top-6 right-6 px-3 py-1 bg-[#F0C4BC] text-[#0E0709] text-[10px] tracking-[0.22em] uppercase">
              Recommended
            </div>

            <div className="flex items-center gap-4 mb-8">
              <span className="overline text-[#F0C4BC]">— The Stationery Wala way</span>
              <span className="ml-auto inline-flex items-center justify-center w-12 h-12 bg-[#F0C4BC] text-[#0E0709]">
                <Check className="w-5 h-5" strokeWidth={2.5} />
              </span>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl text-[#F8EDE7] font-medium mb-1">
              Stationery <span className="italic-serif text-[#F0C4BC]">Wala</span>
            </h3>
            <p className="text-xs tracking-[0.18em] uppercase text-[#F0C4BC]">Score · 10 / 10</p>

            <ul className="mt-10 space-y-7">
              {compareRows.map((row) => (
                <li key={row.label}>
                  <p className="overline text-[#F0C4BC] mb-2">{row.label}</p>
                  <p className="text-base md:text-lg text-[#F8EDE7] font-medium leading-snug flex gap-2 items-start">
                    <Check className="w-4 h-4 mt-1.5 text-[#F0C4BC] flex-shrink-0" />
                    <span>{row.us}</span>
                  </p>
                  <div className="mt-3 h-px bg-[#3A2530]">
                    <div className="h-full bg-[#F0C4BC]" style={{ width: "100%" }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* VS divider */}
          <div className="hidden md:flex absolute inset-y-0 left-1/2 -translate-x-1/2 items-center pointer-events-none z-10">
            <div className="w-20 h-20 rounded-full bg-[#0E0709] border border-[#3A2530] flex items-center justify-center">
              <span className="font-heading italic-serif text-2xl text-[#F0C4BC]">vs</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={2} className="mt-10 flex flex-wrap gap-3">
          {[
            { Icon: ShieldCheck, t: "10-Point Audit" },
            { Icon: Truck, t: "DTDC · Blue Dart" },
            { Icon: Sparkles, t: "Editorial Finish" },
          ].map(({ Icon, t }) => (
            <span key={t} className="inline-flex items-center gap-2 border border-[#3A2530] bg-[#1F1218] px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase text-[#F8EDE7]">
              <Icon className="w-3.5 h-3.5 text-[#F0C4BC]" />
              {t}
            </span>
          ))}
        </Reveal>
      </section>

      {/* PROCESS */}
      <section data-testid="section-process" className="bg-[#1A0E12] text-[#F8EDE7] border-y border-[#3A2530] relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E8B4AC] to-transparent" />
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-7">
              <Reveal>
                <p className="overline text-[#E8B4AC] mb-6">Our Process</p>
                <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl font-normal leading-[0.92] tracking-tight">
                  Four steps.<br/>
                  <span className="italic-serif text-[#F0C4BC]">One obsession.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={2} className="md:col-span-5 text-[#F8EDE7]/65 text-base font-normal leading-relaxed self-end">
              Every project is treated like a studio commission — one team, one standard, from first sketch to your doorstep.
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#3A2530] border border-[#3A2530]">
            {steps.map((s, i) => (
              <Reveal
                key={s.n}
                delay={i + 1}
                className="bg-[#0E0709] p-9 md:p-11 hover:bg-[#2A1820] transition-colors duration-700 group"
                data-testid={`process-step-${i + 1}`}
              >
                <p className="font-heading text-6xl md:text-7xl font-normal text-[#E8B4AC] group-hover:text-[#F5C9C0] transition-colors duration-500">
                  {s.n}
                </p>
                <h3 className="mt-7 font-heading text-3xl text-[#F8EDE7]">{s.t}</h3>
                <p className="mt-3 text-sm font-normal text-[#F8EDE7]/65 leading-relaxed">{s.d}</p>
                <span className="mt-7 block h-px w-12 bg-[#E8B4AC]/40 group-hover:w-full transition-all duration-700" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DELHI NCR EXPRESS BANNER */}
      <section data-testid="section-express-banner" className="relative overflow-hidden bg-gradient-to-b from-[#3A1F2C] via-[#2A1820] to-[#0E0709] border-y border-[#F0C4BC]/30">
        <FloatingPetals count={10} />
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-14 md:py-20 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F0C4BC] text-[#0E0709] text-[10px] tracking-[0.22em] uppercase font-medium mb-5">
                <span className="inline-block w-2 h-2 rounded-full bg-[#0E0709] animate-pulse" />
                Delhi NCR Express
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight font-medium text-[#F8EDE7]">
                Wedding tomorrow?<br/>
                <span className="italic-serif text-[#F0C4BC]">We deliver in 24 hours.</span>
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#E8D5DA] font-normal leading-relaxed max-w-2xl">
                Last-minute mehendi cards, urgent reception invites, same-week hampers — picked, printed and dispatched from our Sector 75 Noida studio overnight.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="grid grid-cols-3 gap-2 md:gap-3 mb-6">
                {[
                  { k: "12h", l: "Design" },
                  { k: "8h", l: "Print" },
                  { k: "4h", l: "Delivery" },
                ].map((s) => (
                  <div key={s.l} className="border border-[#F0C4BC]/40 bg-[#1F1218] p-3 md:p-5 text-center">
                    <p className="font-heading text-2xl md:text-3xl text-[#F0C4BC]">{s.k}</p>
                    <p className="text-[9px] md:text-[10px] tracking-[0.22em] uppercase text-[#C4A8B0] mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/express-delhi-ncr" data-testid="home-express-cta" className="flex-1 min-w-[160px] btn-luxe bg-[#F0C4BC] text-[#0E0709] px-5 py-4 text-[11px] md:text-[12px] tracking-[0.18em] uppercase inline-flex items-center justify-center gap-2 hover:text-[#F8EDE7]">
                  Order in 24 Hours
                </Link>
                <a href={waLink("Hi! I need urgent wedding stationery in Delhi NCR.")} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[140px] btn-outline-luxe border border-[#F0C4BC] text-[#F0C4BC] px-5 py-4 text-[11px] md:text-[12px] tracking-[0.18em] uppercase inline-flex items-center justify-center gap-2 hover:text-[#0E0709]">
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SISTER BRAND — INVITATION WALA */}
      <section data-testid="section-sister-brand" className="relative bg-[#0E0709] border-b border-[#3A2530]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-14 md:py-20">
          <a
            href={SISTER_BRAND.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-sister-brand-link"
            className="group block relative overflow-hidden border border-[#3A2530] hover:border-[#F0C4BC] transition-colors duration-500 bg-gradient-to-br from-[#1F1218] via-[#1A0E12] to-[#2A1820] card-shine"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 p-7 md:p-12 lg:p-14 items-center">
              <div className="md:col-span-7">
                <p className="overline shimmer-text mb-4 inline-flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-[#F0C4BC]" /> Our Sister Brand · Digital Invites
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight font-medium text-[#F8EDE7]">
                  Need a digital<br/>
                  <span className="italic-serif text-[#F0C4BC]">e-invite too?</span>
                </h2>
                <p className="mt-5 text-base md:text-lg text-[#E8D5DA] font-normal leading-relaxed max-w-2xl">
                  Animated GIF invites, video invitations, save-the-dates for WhatsApp — designed by the same team, delivered as digital files. Match it perfectly with your printed suite.
                </p>
                <div className="mt-7 flex flex-wrap gap-3 items-center">
                  <span className="btn-luxe bg-[#F0C4BC] text-[#0E0709] px-7 py-4 text-[12px] tracking-[0.22em] uppercase inline-flex items-center gap-3 group-hover:text-[#F8EDE7]">
                    Visit {SISTER_BRAND.name} <ArrowUpRight className="w-4 h-4" />
                  </span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-[#C4A8B0]">{SISTER_BRAND.domain}</span>
                  <span className="text-[#3A2530] hidden md:inline">·</span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-[#F0C4BC] inline-flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 inline-block">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                    </span>
                    @{SISTER_BRAND.instagramHandle}
                  </span>
                </div>
              </div>
              <div className="md:col-span-5 hidden md:flex md:justify-end">
                <div className="relative w-full max-w-sm">
                  <div className="aspect-[4/5] border border-[#F0C4BC]/30 bg-[#0E0709]/60 backdrop-blur p-7 md:p-9 flex flex-col">
                    <div className="flex items-center justify-between mb-5">
                      <MonitorSmartphone className="w-7 h-7 text-[#F0C4BC]" strokeWidth={1.4} />
                      <span className="text-[10px] tracking-[0.22em] uppercase text-[#F0C4BC] font-mono">.online</span>
                    </div>
                    <div className="flex-1 flex flex-col justify-center text-center">
                      <p className="font-heading text-[42px] md:text-5xl italic-serif text-[#F8EDE7] leading-[0.95]">
                        Invitation
                      </p>
                      <p className="font-heading text-[42px] md:text-5xl italic-serif text-[#F0C4BC] -mt-1 leading-[0.95]">
                        Wala
                      </p>
                      <span className="block w-12 h-px bg-[#F0C4BC] mx-auto my-5" />
                      <p className="text-[11px] tracking-[0.22em] uppercase text-[#C4A8B0]">
                        GIFs · Videos · E-Invites
                      </p>
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#C4A8B0] flex items-center justify-between mt-4">
                      <span>—— Digital Studio</span>
                      <span className="font-mono">2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* LOOKBOOK — REAL PORTFOLIO */}
      <Suspense fallback={<Skeleton />}>
        <div className="lazy-paint">
          <Lookbook />
        </div>
      </Suspense>

      {/* SERVICES PREVIEW */}
      <section data-testid="section-services-preview" className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <Reveal>
            <p className="overline text-[#C4A8B0] mb-6">The Collection</p>
            <h2 className="font-heading text-5xl sm:text-6xl md:text-7xl font-normal leading-[0.92] tracking-tight">
              Pieces, perfected.
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-[#F8EDE7] hover:text-[#F0C4BC] transition-colors border-b border-[#F8EDE7] hover:border-[#F0C4BC] pb-1"
            >
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#3A2530] border border-[#3A2530]">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={i + 1} className="group bg-[#1F1218] relative overflow-hidden" data-testid={`service-card-${s.slug}`}>
              <div className="aspect-[16/10] overflow-hidden">
                <Pic
                  src={s.img}
                  alt={`${s.title} — custom wedding stationery designed and printed by StationeryWalaa`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                />
              </div>
              <div className="p-7 md:p-10 flex items-end justify-between gap-4">
                <div>
                  <p className="overline text-[#C4A8B0] mb-3">{s.tag}</p>
                  <h3 className="font-heading text-3xl md:text-4xl text-[#F8EDE7] group-hover:text-[#F0C4BC] transition-colors">
                    {s.title}
                  </h3>
                </div>
                <Link
                  to="/services"
                  className="shrink-0 w-14 h-14 border border-[#3A2530] flex items-center justify-center group-hover:bg-[#F0C4BC] group-hover:border-[#F0C4BC] transition-colors duration-500"
                  aria-label={`Explore ${s.title}`}
                >
                  <ArrowUpRight className="w-5 h-5 text-[#F8EDE7] group-hover:text-[#0E0709]" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        {/* WEDDING STATIONERY INDEX — internal links to landing pages */}
        <Reveal delay={2} className="mt-12 pt-8 border-t border-[#3A2530]" data-testid="home-stationery-index">
          <div className="flex flex-wrap gap-x-6 gap-y-3 items-center">
            <span className="overline text-[#C4A8B0]">Explore Wedding Stationery:</span>
            {STATIONERY_NAV.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`home-stationery-${s.slug}`}
                className="text-[11px] tracking-[0.16em] uppercase text-[#F8EDE7] hover:text-[#F0C4BC] border-b border-transparent hover:border-[#F0C4BC] transition-colors pb-0.5"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* SAMPLE KIT — LEAD MAGNET */}
      <Suspense fallback={<Skeleton />}>
        <div className="lazy-paint">
          {/* TESTIMONIALS — Worldwide couples */}
          <Testimonials />

          <SampleKit />

          {/* FROM THE JOURNAL */}
          <JournalTeaser />

          {/* FAQ */}
          <FAQ />
        </div>
      </Suspense>

      {/* TESTIMONIAL BANNER */}
      <section data-testid="section-quote-banner" className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
        <Reveal className="border border-[#F8EDE7] p-12 md:p-20 bg-[#0E0709] grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="overline text-[#C4A8B0] mb-5">A note from our clients</p>
            <p className="font-heading text-3xl md:text-5xl font-medium italic-serif leading-snug text-[#F8EDE7]">
              &ldquo;Budget pricing. Boutique feel. Every. Single. Time.&rdquo;
            </p>
            <p className="mt-5 text-sm text-[#E8D5DA] tracking-wide">— what we hear, on repeat.</p>
            <div className="mt-7"><Flourish className="w-32 opacity-60" /></div>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/book"
              data-testid="banner-book-cta"
              className="btn-luxe bg-[#F0C4BC] text-[#0E0709] px-9 py-[18px] text-[12px] tracking-[0.22em] uppercase inline-flex items-center gap-3"
            >
              Begin a Project <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
